import React, { useState, useEffect } from 'react';
import InteractiveAccordion from '../components/InteractiveAccordion';
import TestimonialCarousel from '../components/TestimonialCarousel';
import { TrophyIcon, PeopleIcon, LightbulbIcon, GlobeIcon, RocketIcon } from '../components/AnimatedIcons';
import { Link } from 'react-router-dom';

const About = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  const carouselItems = [
    {
      image: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=1200&h=600&fit=crop",
      title: "Kehosa Institute for Strategic Studies",
      subtitle: "Leading Strategic Thinking in Kenyan Politics",
      description: "KISS is dedicated to advancing strategic studies in politics and democratic processes through research, education, and partnership-driven excellence.",
      cta: "Learn Our Mission"
    },
    {
      image: "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=1200&h=600&fit=crop",
      title: "Expert Leadership Team",
      subtitle: "Experienced Political Strategists & Researchers",
      description: "Our team combines academic rigor with practical political experience to deliver unparalleled strategic insights and leadership development.",
      cta: "Meet Our Team"
    },
    {
      image: "https://images.unsplash.com/photo-1589391886645-d51941baf7fb?w=1200&h=600&fit=crop",
      title: "Strategic Partnerships",
      subtitle: "Collaborative Excellence in Research",
      description: "In partnership with Democracy Institute (DI) and iTarget Media EA, we deliver comprehensive strategic solutions for political success.",
      cta: "View Partnerships"
    }
  ];

  useEffect(() => {
    if (!isHovered) {
      const timer = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % carouselItems.length);
      }, 6000);
      return () => clearInterval(timer);
    }
  }, [isHovered, carouselItems.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % carouselItems.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + carouselItems.length) % carouselItems.length);
  };
  const teamMembers = [
    {
      name: "Dr. Sarah Wanjiku",
      position: "Director, Kehosa Institute for Strategic Studies",
      bio: "PhD in Political Science from University of Nairobi. Leading strategic thinking in Kenyan politics and democratic processes. Co-founder of the Kenya Democracy and Leadership Project.",
      expertise: ["Strategic Studies", "Democratic Processes", "Leadership Development"],
      image: "SW"
    },
    {
      name: "Prof. James Kiprop",
      position: "Director, Democracy Institute (DI)",
      bio: "Professor of Political Science and International Relations. Expert in democratic governance, electoral systems, and political strategy. Co-developer of the 10 Keys to Victory™ framework.",
      expertise: ["Democratic Theory", "Electoral Systems", "Strategic Analysis"],
      image: "JK"
    },
    {
      name: "Hon. Grace Achieng",
      position: "Executive Director, Strategy101 Program",
      bio: "Former Member of Parliament and political strategist. Expert in campaign execution and leadership development. Graduate of the inaugural DI-KISS masterclass program.",
      expertise: ["Campaign Strategy", "Leadership", "Policy Development"],
      image: "GA"
    },
    {
      name: "Dr. David Oduya",
      position: "Director, iTarget Media EA Partnership",
      bio: "PhD in Communication Studies. Digital media expert specializing in political communication and strategic messaging. Leads the technological integration of KISS programs.",
      expertise: ["Digital Strategy", "Political Communication", "Media Technology"],
      image: "DO"
    }
  ];

  const milestones = [
    { year: "2015", event: "Kehosa Institute for Strategic Studies (KISS) established" },
    { year: "2017", event: "Partnership formed with Democracy Institute (DI)" },
    { year: "2019", event: "Launched Kenya Democracy and Leadership Project" },
    { year: "2020", event: "Developed Strategy101: Campaign Options framework" },
    { year: "2022", event: "Launched 10 Keys to Victory™ training program" },
    { year: "2023", event: "Partnership with iTarget Media EA for digital integration" },
    { year: "2024", event: "Trained 1000+ political leaders across all levels" }
  ];

  const values = [
    {
      title: "Strategy is the Foundation of Success",
      description: "We believe that strategic thinking is the cornerstone of all political achievement and democratic progress.",
      icon: "bi-bullseye"
    },
    {
      title: "Evidence-Based Research",
      description: "All our programs and recommendations are grounded in rigorous research and empirical evidence.",
      icon: "bi-bar-chart-line"
    },
    {
      title: "Democratic Excellence",
      description: "We are committed to strengthening democratic institutions and processes across Kenya.",
      icon: "bi-building"
    },
    {
      title: "Inclusive Leadership",
      description: "Developing leaders who represent and serve all segments of Kenyan society.",
      icon: "bi-people"
    },
    {
      title: "Strategic Innovation",
      description: "Combining traditional wisdom with modern strategic methodologies for optimal results.",
      icon: "bi-lightbulb"
    }
  ];

  const faqs = [
    {
      title: "What is the Kehosa Institute for Strategic Studies (KISS)?",
      content: "KISS is Kenya's premier institute dedicated to strategic studies in politics and democratic processes. We provide comprehensive training and research programs designed to enhance the strategic capabilities of political leaders at all levels.",
      details: [
        "Strategic training programs for political leaders",
        "Research in democratic processes and governance",
        "Partnerships with leading academic and strategic institutions",
        "Focus on evidence-based strategic methodologies"
      ]
    },
    {
      title: "What is the 10 Keys to Victory™ Program?",
      content: "The 10 Keys to Victory™ is our comprehensive training program that covers the essential strategic competencies required for electoral success. From campaign strategy to voter mobilization, this program equips candidates with the tools needed to win elections.",
      details: [
        "10 comprehensive training modules",
        "Covers all aspects of successful campaigning",
        "Designed for all electoral levels",
        "Based on empirical research and proven strategies"
      ]
    },
    {
      title: "Who can benefit from KISS programs?",
      content: "Our programs are designed for presidential, gubernatorial, parliamentary, and county assembly candidates and leaders. We also provide strategic consultation for political parties and organizations committed to democratic excellence.",
      details: [
        "Presidential candidates and campaigns",
        "Gubernatorial and county-level candidates",
        "Parliamentary candidates (National Assembly and Senate)",
        "County assembly representatives",
        "Political party leadership and strategists"
      ]
    },
    {
      title: "What is your success rate?",
      content: "Our clients have achieved an 85% success rate in their electoral objectives. Success is measured not just by wins, but by campaigns that strengthen democratic participation and community engagement.",
      details: [
        "85% electoral success rate for our clients",
        "Multiple landslide victories achieved",
        "Consistent performance across election types",
        "Focus on sustainable political success"
      ]
    }
  ];

  const testimonials = [
    {
      quote: "Kehosa Election Consultancy Agency transformed our campaign from a local effort to a county-wide movement. Their strategic insights and grassroots approach delivered results beyond our expectations.",
      name: "Hon. Mary Wanjiku",
      position: "County Governor",
      region: "Kiambu County"
    },
    {
      quote: "The digital campaign strategies implemented by their team helped us reach young voters like never before. Our social media engagement increased by 300% during the campaign period.",
      name: "Sen. David Kiprop",
      position: "Senator",
      region: "Elgeyo-Marakwet County"
    },
    {
      quote: "Their data-driven approach to voter targeting was instrumental in our parliamentary victory. We identified and mobilized previously untapped voter segments that proved decisive.",
      name: "MP Grace Achieng",
      position: "Member of Parliament",
      region: "Kisumu County"
    }
  ];

  return (
    <div>
      {/* Interactive Hero Carousel */}
      <section
        className="relative w-full overflow-hidden rounded-b-2xl shadow-2xl"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div
          className="flex transition-transform duration-700 ease-in-out"
          style={{ transform: `translateX(-${currentSlide * 100}%)` }}
        >
          {carouselItems.map((item, index) => (
            <div key={index} className="w-full flex-shrink-0">
              <div className="relative h-96 md:h-[500px] bg-gradient-to-br from-secondary-600 to-secondary-800">
                <div className="absolute inset-0 bg-black bg-opacity-40"></div>
                <img
                  src={item.image}
                  alt={item.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center text-white max-w-4xl px-4">
                    <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold mb-2 text-shadow">
                      {item.title}
                    </h1>
                    <h2 className="text-xl md:text-2xl lg:text-3xl font-semibold mb-4 text-secondary-200">
                      {item.subtitle}
                    </h2>
                    <p className="text-lg md:text-xl mb-8 text-secondary-100 leading-relaxed">
                      {item.description}
                    </p>
                    <Link
                      to="#mission"
                      className="btn-primary bg-white text-secondary-600 hover:bg-secondary-50 text-lg py-3 px-8 inline-block transform hover:scale-105 transition-all duration-300"
                    >
                      {item.cta}
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Navigation Arrows */}
        <button
          onClick={prevSlide}
          className="absolute left-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-all duration-300 z-10"
          aria-label="Previous slide"
        >
          <i className="bi bi-chevron-left text-xl"></i>
        </button>
        <button
          onClick={nextSlide}
          className="absolute right-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-all duration-300 z-10"
          aria-label="Next slide"
        >
          <i className="bi bi-chevron-right text-xl"></i>
        </button>

        {/* Slide Indicators */}
        <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex space-x-2 z-10">
          {carouselItems.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                index === currentSlide
                  ? 'bg-white scale-125'
                  : 'bg-white/50 hover:bg-white/75'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="bg-white section-padding">
        <div className="container-max">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-secondary-900 mb-4 sm:mb-6">
                Our Mission
              </h2>
              <p className="text-base sm:text-lg lg:text-xl text-secondary-600 mb-4 sm:mb-6 leading-relaxed">
                To advance strategic thinking in Kenyan politics through research, education, and training programs
                that strengthen democratic institutions and develop effective political leadership at all levels.
              </p>
              <div className="space-y-3 sm:space-y-4">
                <div className="flex items-start space-x-3 sm:space-x-4">
                  <div className="w-6 h-6 sm:w-8 sm:h-8 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-primary-600 font-bold text-sm sm:text-base">✓</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-secondary-900 text-sm sm:text-base">Strategic Excellence</h4>
                    <p className="text-secondary-600 text-sm sm:text-base">Strategy is the foundation of all political success</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3 sm:space-x-4">
                  <div className="w-6 h-6 sm:w-8 sm:h-8 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-primary-600 font-bold text-sm sm:text-base">✓</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-secondary-900 text-sm sm:text-base">Research-Driven</h4>
                    <p className="text-secondary-600 text-sm sm:text-base">Evidence-based approaches to political strategy</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3 sm:space-x-4">
                  <div className="w-6 h-6 sm:w-8 sm:h-8 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-primary-600 font-bold text-sm sm:text-base">✓</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-secondary-900 text-sm sm:text-base">Democratic Development</h4>
                    <p className="text-secondary-600 text-sm sm:text-base">Strengthening Kenya's democratic institutions</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-primary-50 to-secondary-50 p-8 rounded-3xl">
              <h3 className="text-xl sm:text-2xl font-bold text-secondary-900 mb-4 sm:mb-6">Our Vision</h3>
              <p className="text-base sm:text-lg text-secondary-600 mb-4 sm:mb-6">
                To be Kenya's premier institute for strategic studies, advancing democratic excellence
                through research, education, and strategic leadership development that serves the nation.
              </p>
              <div className="grid grid-cols-2 gap-3 sm:gap-4">
                <div className="text-center">
                  <div className="text-2xl sm:text-3xl font-bold text-primary-600 mb-1 sm:mb-2">10</div>
                  <div className="text-xs sm:text-sm text-secondary-600">Keys to Victory</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl sm:text-3xl font-bold text-primary-600 mb-1 sm:mb-2">1000+</div>
                  <div className="text-xs sm:text-sm text-secondary-600">Leaders Trained</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl sm:text-3xl font-bold text-primary-600 mb-1 sm:mb-2">47</div>
                  <div className="text-xs sm:text-sm text-secondary-600">Counties</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl sm:text-3xl font-bold text-primary-600 mb-1 sm:mb-2">85%</div>
                  <div className="text-xs sm:text-sm text-secondary-600">Success Rate</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Company Timeline */}
      <section className="bg-secondary-50 section-padding">
        <div className="container-max">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-secondary-900 mb-4">
              Our Journey
            </h2>
            <p className="text-xl text-secondary-600 max-w-3xl mx-auto">
              Two decades of innovation, success, and commitment to Kenya's democratic development
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            {milestones.map((milestone, index) => (
              <div key={index} className="flex items-center mb-8">
                <div className="flex-shrink-0 w-20 h-20 bg-primary-600 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg">
                  {milestone.year}
                </div>
                <div className="ml-8 flex-1">
                  <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow duration-300">
                    <p className="text-lg text-secondary-700">{milestone.event}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="bg-white section-padding">
        <div className="container-max">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-secondary-900 mb-4">
              Our Values
            </h2>
            <p className="text-xl text-secondary-600 max-w-3xl mx-auto">
              The principles that guide every campaign we manage and every decision we make
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-secondary-50 to-primary-50 p-8 rounded-2xl text-center hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 animate-fade-in"
                style={{animationDelay: `${index * 0.2}s`}}
              >
                <div className="text-6xl mb-4 text-primary-600">
                  {value.icon === 'bi-balance-scale' && <TrophyIcon />}
                  {value.icon === 'bi-bar-chart-line' && <GlobeIcon />}
                  {value.icon === 'bi-people' && <PeopleIcon />}
                  {value.icon === 'bi-rocket' && <RocketIcon />}
                </div>
                <h3 className="text-xl font-bold text-secondary-900 mb-3">{value.title}</h3>
                <p className="text-secondary-600 leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="bg-secondary-900 text-white section-padding">
        <div className="container-max">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Meet Our Team
            </h2>
            <p className="text-xl text-secondary-300 max-w-3xl mx-auto">
              Experienced political strategists, digital experts, and campaign professionals
              dedicated to your electoral success
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {teamMembers.map((member, index) => (
              <div
                key={index}
                className="bg-secondary-800 rounded-2xl p-6 hover:bg-secondary-700 transition-colors duration-300 animate-fade-in"
                style={{animationDelay: `${index * 0.15}s`}}
              >
                <div className="w-20 h-20 rounded-full mx-auto mb-4 overflow-hidden shadow-lg">
                  <img
                    src={
                      member.name.includes("Dr. Sarah Wanjiku")
                        ? "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=160&h=160&fit=crop&crop=face"
                        : member.name.includes("Prof. James Kiprop")
                        ? "https://images.unsplash.com/photo-1560250097-0b93528c311a?w=160&h=160&fit=crop&crop=face"
                        : member.name.includes("Hon. Grace Achieng")
                        ? "https://images.unsplash.com/photo-1580489944761-15a19d654956?w=160&h=160&fit=crop&crop=face"
                        : "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=160&h=160&fit=crop&crop=face"
                    }
                    alt={member.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="text-center">
                  <h3 className="text-xl font-bold mb-2">{member.name}</h3>
                  <p className="text-primary-400 font-semibold mb-3">{member.position}</p>
                  <p className="text-secondary-300 text-sm mb-4 leading-relaxed">{member.bio}</p>
                  <div className="space-y-1">
                    {member.expertise.map((skill, skillIndex) => (
                      <span key={skillIndex} className="inline-block bg-primary-600 text-xs px-2 py-1 rounded-full mr-2 mb-2">
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <TestimonialCarousel testimonials={testimonials} />

      {/* FAQ Section */}
      <section className="bg-white section-padding">
        <div className="container-max">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-secondary-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-secondary-600 max-w-3xl mx-auto">
              Get answers to common questions about our services and approach
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <InteractiveAccordion items={faqs} />
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="bg-gradient-to-r from-primary-600 to-primary-800 text-white section-padding">
        <div className="container-max text-center">
          <h2 className="text-4xl md:text-6xl font-bold mb-6">
            Ready to Work With Us?
          </h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto text-primary-100">
            Join the ranks of successful political leaders who have achieved their goals
            with our strategic expertise and proven methodologies.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="btn-primary bg-white text-primary-600 hover:bg-secondary-50 transform hover:scale-105 transition-transform duration-200">
              Schedule Consultation
            </button>
            <button className="btn-secondary bg-transparent border-white text-white hover:bg-white hover:text-primary-600 transform hover:scale-105 transition-transform duration-200">
              Download Company Profile
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;